import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, LogOut, Plus } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useBranding } from '@/contexts/BrandingContext'
import { useRole } from '@/hooks/useRole'
import { useAuth } from '@/hooks/useAuth'
import { useConversations } from '@/hooks/useConversations'
import { AssistantLauncher } from '@/components/assistant/AssistantLauncher'
import { BrandMark } from '@/components/BrandMark'
import { cn } from '@/lib/utils'
import {
  CALENDAR_ITEM,
  MAIN_SECTIONS,
  NEW_SALE_ITEM,
  SETTINGS_ENTRY,
  SETTINGS_GROUPS,
  activeHref,
  backLabel,
  isSettingsHref,
  knownHref,
  sectionFor,
  type NavItem,
} from './navItems'

// Above this width the nav is a permanent rail; below it, a slide-in drawer.
// Chosen so a landscape tablet keeps the rail but a portrait one does not.
const WIDE_BREAKPOINT = 900

interface NavState {
  /**
   * False outside a NavShell, where every action below is a no-op. Screens must
   * check it before drawing an affordance that opens the drawer — the same rule
   * as useCopiedClient().available: a button that could only do nothing is worse
   * than no button.
   */
  available: boolean
  /** True when the permanent sidebar is showing (so screens can hide the menu button). */
  isWide: boolean
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
}

const NavContext = createContext<NavState>({
  available: false,
  isWide: false,
  drawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  toggleDrawer: () => {},
})

export function useNav(): NavState {
  return useContext(NavContext)
}

/** Replaces useWindowDimensions(). matchMedia fires only on breakpoint crossings
 *  rather than on every resize pixel, so this re-renders far less than the RN
 *  version did. */
function useIsWide(): boolean {
  const query = `(min-width: ${WIDE_BREAKPOINT}px)`
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )

  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setIsWide(e.matches)
    setIsWide(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])

  return isWide
}

// ─── Nav list (shared by the rail and the drawer) ──────────────────────────────

// Where "Back" in the Settings menu returns to: the last screen outside Settings.
// Module-level because the drawer's list unmounts whenever the drawer closes.
let lastMainPath = { pathname: CALENDAR_ITEM.href, search: '' }

function NavRow({
  item,
  active,
  badgeCount,
  showHint,
  onPress,
}: {
  item: Pick<NavItem, 'label' | 'icon' | 'hint'>
  active: boolean
  badgeCount?: number
  showHint?: boolean
  onPress: () => void
}) {
  const branding = useBranding()
  const Icon = item.icon
  return (
    <button
      type="button"
      onClick={onPress}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 text-left text-foreground/75 transition-colors',
        showHint ? 'py-2' : 'py-[7px]',
        'hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
      )}
      style={active ? { backgroundColor: `${branding.primaryColor}14`, color: branding.primaryColor } : undefined}
    >
      <Icon size={16} className={cn('shrink-0', !active && 'text-muted-foreground')} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-[13.5px]', active ? 'font-semibold' : 'font-medium')}>
          {item.label}
        </span>
        {showHint && item.hint ? (
          <span className="mt-px block truncate text-[11.5px] font-normal text-muted-foreground">{item.hint}</span>
        ) : null}
      </span>
      {badgeCount ? (
        <span className="min-w-[18px] rounded-full bg-rose-500 px-1.5 text-center text-[10.5px] leading-[18px] font-semibold text-white">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : null}
    </button>
  )
}

function NavList({ showHints, onNavigate }: { showHints?: boolean; onNavigate?: () => void }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const branding = useBranding()
  const { tenantConfig } = useRole()
  const { signOut, authState } = useAuth()
  // Poll slowly: the badge is ambient, and the inbox screen polls fast on its own.
  const { needsResponseCount } = useConversations(30000)

  const current = activeHref(pathname)
  const inSettings = isSettingsHref(current)
  const currentSection = sectionFor(current)
  const email = authState.status === 'authenticated' ? authState.user.email ?? '' : ''
  const backTo = backLabel(knownHref(lastMainPath.pathname))

  const go = useCallback((href: string) => {
    onNavigate?.()
    navigate(href)
  }, [navigate, onNavigate])

  const groupTitle = 'px-2.5 pb-1 text-[11.5px] font-medium text-muted-foreground'

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-3">
        <BrandMark
          name={tenantConfig?.name}
          logoUrl={branding.logoUrl}
          color={branding.primaryColor}
          size={30}
          shape="squircle"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold text-foreground">{tenantConfig?.name ?? 'Dashboard'}</div>
          {email ? <div className="mt-px truncate text-[11.5px] text-muted-foreground">{email}</div> : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pt-1 pb-3">
        {inSettings ? (
          // Inside Settings the menu IS the settings list, so the daily menu stays
          // short; Back returns to the screen you came from.
          <>
            <button
              type="button"
              onClick={() => go(`${lastMainPath.pathname}${lastMainPath.search}`)}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-[7px] text-left text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft size={16} className="shrink-0" aria-hidden />
              Back to {backTo}
            </button>
            <div className="px-2.5 pt-3 text-[15px] font-semibold text-foreground">Settings</div>
            {SETTINGS_GROUPS.map((group) => (
              <div key={group.title} className="mt-3.5">
                <div className={groupTitle}>{group.title}</div>
                <div className="space-y-px">
                  {group.items.map((item) => (
                    <NavRow
                      key={item.href}
                      item={item}
                      active={current === item.href}
                      showHint={showHints}
                      onPress={() => go(item.href)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {/* No Chat row here on purpose (user request 2026-08-10): the
                assistant panel's floating button is the one way in. */}
            <button
              type="button"
              onClick={() => go(NEW_SALE_ITEM.href)}
              aria-current={current === NEW_SALE_ITEM.href ? 'page' : undefined}
              className="flex h-9 w-full items-center justify-center gap-1.5 rounded-md text-[13.5px] font-semibold text-white shadow-xs transition-[filter] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none aria-[current=page]:ring-2 aria-[current=page]:ring-offset-2"
              style={{ backgroundColor: branding.primaryColor }}
            >
              <Plus size={16} aria-hidden />
              {NEW_SALE_ITEM.label}
            </button>

            <div className="mt-3 space-y-px">
              {MAIN_SECTIONS.map((section) => (
                <NavRow
                  key={section.label}
                  item={section}
                  active={currentSection === section}
                  badgeCount={section.badge === 'inbox' ? needsResponseCount : undefined}
                  showHint={showHints}
                  onPress={() => go(section.items[0].href)}
                />
              ))}
            </div>

            <div className="mt-3 border-t border-border pt-3">
              <NavRow
                item={SETTINGS_ENTRY}
                active={false}
                showHint={showHints}
                onPress={() => go(SETTINGS_ENTRY.href)}
              />
            </div>
          </>
        )}

        <div className="mt-4 border-t border-border pt-2">
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => { onNavigate?.(); signOut() }}
          >
            <LogOut size={16} className="shrink-0" aria-hidden />
            <span className="text-[13.5px] font-medium">Sign out</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export function NavShell({ children }: { children: React.ReactNode }) {
  const isWide = useIsWide()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Crossing up to the rail must dismiss the drawer, or it stays mounted
  // off-screen and traps focus behind the sidebar.
  useEffect(() => {
    if (isWide) setDrawerOpen(false)
  }, [isWide])

  // Tracked here, not in NavList, because a closed drawer's list is not mounted.
  const { pathname, search } = useLocation()
  useEffect(() => {
    const href = knownHref(pathname)
    if (href && !isSettingsHref(href)) lastMainPath = { pathname, search }
  }, [pathname, search])

  const value = useMemo<NavState>(() => ({
    available: true,
    isWide,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    toggleDrawer: () => setDrawerOpen((v) => !v),
  }), [isWide, drawerOpen])

  return (
    <NavContext.Provider value={value}>
      {/* h-full and NOTHING else. `min-h-screen` used to sit here as a floor and
          it is the bug that made the menu unreachable in the installed PWA:
          min-height is 100vh, the FULL viewport, while this div's container
          (#root) is only `100vh - safe-area-top - safe-area-bottom` because
          index.css pads the body clear of the notch and home indicator. The
          shell was therefore forced taller than the space it had, the DOCUMENT
          scrolled by exactly the inset total, and the screen header — with the
          only menu button on it — slid off the top with no way back.
          In a browser tab the insets are 0, which is why this was invisible
          until the app was installed. html/body/#root are all height:100%, so
          h-full already resolves against a definite height and needs no floor. */}
      <div className="flex h-full bg-muted/40">
        {isWide && (
          <aside className="w-[232px] shrink-0 border-r border-border bg-background">
            <NavList />
          </aside>
        )}

        <div className="min-w-0 flex-1">{children}</div>

        {/* The assistant panel docks as the row's right-most column on wide
            screens and overlays from the right below that. Mounted here (not
            per screen) so the conversation survives navigation; the launcher
            lazy-loads the heavy body on first open. */}
        <AssistantLauncher />

        {/* Drawer only exists on narrow screens; the rail replaces it above the
            breakpoint, so both can never be on screen at once. */}
        {!isWide && (
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent side="left" className="w-72 gap-0 bg-background p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <NavList showHints onNavigate={() => setDrawerOpen(false)} />
            </SheetContent>
          </Sheet>
        )}
      </div>
    </NavContext.Provider>
  )
}
